import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import {
  formatQuantityDisplay,
  getWholeDirectKgQuantityInputAttributes,
  getQuantityInputMode,
  getQuantityInputStep,
  parseNonNegativeQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import type { EnrichedCheckinItem } from "@/features/checkin/types";

type ReceivingQuantityPanelProps = {
  open: boolean;
  item: EnrichedCheckinItem;
  pendingQuantity: number;
  initialQuantity: number;
  pending: boolean;
  onConfirm: (quantity: number) => Promise<void>;
  onValidationError: () => void;
  onCancel: () => void;
};

export function ReceivingQuantityPanel({
  open,
  item,
  pendingQuantity,
  initialQuantity,
  pending,
  onConfirm,
  onValidationError,
  onCancel,
}: ReceivingQuantityPanelProps) {
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const isWholeDirectKg =
    item.unit_type === "kg" && item.direct_kg_quantity_mode === "whole";
  const quantityInputAttributes = isWholeDirectKg
    ? getWholeDirectKgQuantityInputAttributes()
    : {
        min: 0,
        step: getQuantityInputStep(item.unit_type),
        inputMode: getQuantityInputMode(item.unit_type) as
          | "decimal"
          | "numeric",
      };

  useEffect(() => {
    if (!open) return;
    setValue(formatQuantityDisplay(initialQuantity, item.unit_type));
    setLocalError(null);
  }, [initialQuantity, item.unit_type, open]);

  async function submit() {
    if (isWholeDirectKg) {
      const validation = validateWholeDirectKgQuantityInput(value);
      if (validation.quantity == null) {
        setLocalError(null);
        onValidationError();
        return;
      }

      setLocalError(null);
      await onConfirm(validation.quantity);
      return;
    }

    const quantity = parseNonNegativeQuantityInput(value, item.unit_type);
    if (!quantity || quantity <= 0) {
      setLocalError("Informe a quantidade que acabou de chegar.");
      return;
    }
    setLocalError(null);
    await onConfirm(quantity);
  }

  if (!open) return null;
  return (
    <div className="receiving-panel">
      <label className="field" htmlFor={`receive-now-${item.id}`}>
        <span>Quantidade recebida agora</span>
        <input
          id={`receive-now-${item.id}`}
          type="number"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onFocus={(event) => event.target.select()}
          {...quantityInputAttributes}
        />
      </label>
      <button
        type="button"
        className="action-btn receiving-confirm"
        onClick={() => void submit()}
        disabled={pending}
      >
        {pending ? <span className="spinner-small" /> : <Check size={17} />}{" "}
        <span className="receiving-confirm-full">Confirmar recebimento</span>
        <span className="receiving-confirm-short">Confirmar</span>
      </button>
      <button
        type="button"
        className="action-btn receiving-cancel"
        onClick={onCancel}
        disabled={pending}
      >
        Cancelar
      </button>
      {pendingQuantity > 0 ? (
        <span className="receiving-hint">
          Pendente: {formatQuantityDisplay(pendingQuantity, item.unit_type)}
        </span>
      ) : null}
      {localError ? (
        <span className="receiving-error" role="alert">
          {localError}
        </span>
      ) : null}
    </div>
  );
}
